// Mirror of chave-cmb-api notification DTOs. Keep in sync.

export type TriggerType =
  | 'alarm_active'
  | 'alarm_recurrent'
  | 'box_offline'
  | 'alarm_unattended'
  | 'alarm_burst'
  | 'box_silent'
  | 'command_failed'
  | 'group_alarm'
  | 'energy_outage'
  | 'token_revoked';

export type ActionChannel = 'email' | 'whatsapp' | 'webhook';
export type RoleSelector = 'admin' | 'manager' | 'coordinator' | 'operator';

export type AlarmTypeKey =
  | 'door' | 'bolt' | 'door_lock' | 'vibration' | 'energy' | 'sound'
  | 'violation' | 'request_opening_of_safe'
  | 'maximum_time_for_open_door' | 'cable_cut_alarm';

export type RepeatStrategy = 'none' | 'fixed' | 'escalation' | 'daily';

export interface NotificationRuleTriggerDTO {
  id?: number;
  trigger_type: TriggerType;
  params: Record<string, unknown>;
  // Re-notification cadence. Premium-tier feature — the rule editor
  // locks anything other than 'none' when company_notifications_premium
  // is false. Backend re-checks (assertPremiumIfNeededForTriggers).
  repeat_strategy?: RepeatStrategy;
  repeat_interval_minutes?: number | null;
  escalation_steps_minutes?: number[] | null;
}

export interface NotificationRuleActionDTO {
  id?: number;
  channel: ActionChannel;
  recipients: {
    emails?: string[];
    phones?: string[];
    roles?: RoleSelector[];
    url?: string;
  };
  template?: string | null;
  params?: {
    subject?: string;
    headers?: Record<string, string>;
    method?: 'POST' | 'PUT';
  };
}

export interface NotificationRule {
  id: number;
  name: string;
  description: string | null;
  scope: 'global' | 'company';
  company_id: number | null;
  enabled: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  triggers: Array<{
    id: number;
    trigger_type: TriggerType;
    params: Record<string, unknown>;
    repeat_strategy: RepeatStrategy;
    repeat_interval_minutes: number | null;
    escalation_steps_minutes: number[] | null;
  }>;
  actions: Array<{
    id: number;
    channel: ActionChannel;
    recipients: Record<string, unknown>;
    template: string | null;
    params: Record<string, unknown>;
  }>;
}

export interface CompanyEvolutionApi {
  id: number;
  company_id: number;
  base_url: string;
  instance_name: string;
  api_key_masked: string;
  connected: boolean;
  last_sync_at: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface RuleEvent {
  id: string;
  rule_id: number;
  trigger_type: string;
  box_id: number | null;
  company_id: number | null;
  payload: Record<string, unknown>;
  delivery_status: Record<string, unknown>;
  fired_at: string;
  created_at: string;
}

export const TRIGGER_LABELS: Record<TriggerType, string> = {
  alarm_active:                'Alarme ativo (em tempo real)',
  alarm_recurrent:             'Alarmes recorrentes (X em N dias)',
  box_offline:                 'Caixa offline há X minutos',
  alarm_unattended:            'Alarme não atendido em X minutos',
  alarm_burst:                 'Mesmo alarme N vezes em X minutos',
  box_silent:                  'Caixa silenciosa há X dias',
  command_failed:              'Comando enviado falhou',
  group_alarm:                 'Alarmes em grupo de caixas',
  energy_outage:               'Queda de energia em várias caixas',
  token_revoked:               'Token de dispositivo revogado',
};

export const CHANNEL_LABELS: Record<ActionChannel, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  webhook: 'Webhook',
};

export const ROLE_LABELS: Record<RoleSelector, string> = {
  admin: 'Administradores',
  manager: 'Gerentes',
  coordinator: 'Coordenadores',
  operator: 'Operadores',
};

export const ALARM_TYPE_LABELS: Record<AlarmTypeKey, string> = {
  door: 'Porta',
  bolt: 'Trava',
  door_lock: 'Fechadura',
  vibration: 'Vibração',
  energy: 'Energia',
  sound: 'Som',
  violation: 'Violação',
  request_opening_of_safe: 'Abertura do cofre',
  maximum_time_for_open_door: 'Porta aberta há muito',
  cable_cut_alarm: 'Cabo cortado',
};

export const ALARM_TYPE_VALUES: AlarmTypeKey[] = Object.keys(ALARM_TYPE_LABELS) as AlarmTypeKey[];

export const REPEAT_STRATEGY_LABELS: Record<RepeatStrategy, string> = {
  none: 'Não repete (1 alerta por ocorrência)',
  fixed: 'A cada X horas enquanto persistir',
  escalation: 'Escalonado (vários níveis crescentes)',
  daily: 'Lembrete diário enquanto persistir',
};

export const REPEAT_STRATEGY_DESCRIPTIONS: Record<RepeatStrategy, string> = {
  none: 'Comportamento padrão. Dispara uma vez quando o gatilho bate e fica em silêncio até a condição voltar ao normal e ocorrer novamente.',
  fixed: 'Re-envia a notificação a cada intervalo fixo (ex: a cada 4 horas) enquanto a condição persistir. Útil pra operadores que podem perder o primeiro alerta.',
  escalation: 'Notifica em momentos crescentes a partir do primeiro disparo (ex: 30min, 2h, 8h, 24h). Após o último passo, para. Bom pra SLAs com escalation.',
  daily: 'Envia no máximo um alerta por dia (calendário de São Paulo) enquanto a condição persistir. Lembrete suave pra condições crônicas.',
};

/**
 * Trigger types that support reminder strategies. Reminders only make
 * sense for ONGOING conditions where the engine can keep evaluating
 * the same box over time — point-in-time events (alarm_active fires
 * once per fire) don't benefit from re-notification at the rule level.
 */
export const REPEAT_SUPPORTED_TRIGGERS: TriggerType[] = ['box_offline', 'alarm_unattended'];

/** Friendly escalation presets the editor offers as one-click defaults. */
export const ESCALATION_PRESETS: Array<{ label: string; steps: number[] }> = [
  { label: 'Curto (30m / 2h / 8h)', steps: [30, 120, 480] },
  { label: 'Padrão (30m / 2h / 8h / 24h)', steps: [30, 120, 480, 1440] },
  { label: 'Estendido (1h / 4h / 12h / 24h / 72h)', steps: [60, 240, 720, 1440, 4320] },
];
